import React from "react";
import { Button } from "@/components/ui/button";
import { Check, Wrench, Headphones, Snowflake, Settings2 } from "lucide-react";
import svc1 from "@/assets/svc-office-desk.webp";
import svc3 from "@/assets/svc-team-3.png";
import svc4 from "@/assets/svc-boxes.png";

const MAINTENANCE = [
  "Regular blade changes",
  "Full cleaning of the mower and chassis",
  "Cutting head strip-down and deep cleaning",
  "Component and wear checks",
  "Safety and function testing",
  "Fault-history review",
  "Operational and performance checks",
  "Charging station cleaned and inspected",
  "Charger voltage output tested",
];

const PRIORITY = [
  "Telephone support",
  "Priority breakdown response",
  "Out-of-hours call-out availability when required",
  "Ongoing monitoring of mower condition",
  "Proactive identification of developing maintenance requirements",
];

const WINTER = [
  "Collection for winter servicing",
  "Full winter service with no additional servicing charge",
  "Secure off-site winter storage",
  "Pre-season preparation",
  "Delivery back to site ready for the new mowing season",
];

function CheckList({ items }) {
  return (
    <ul className="mt-4 space-y-2">
      {items.map((i) => (
        <li key={i} className="flex items-start gap-2 text-brand-ink text-sm">
          <Check className="h-4 w-4 text-brand-navy mt-0.5 shrink-0" /> {i}
        </li>
      ))}
    </ul>
  );
}

export function CommercialMaintenance({ onEnquire }) {
  return (
    <section className="bg-brand-white border-y border-brand-navy/10" data-testid="commercial-maintenance">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="max-w-3xl">
          <span className="font-mono-spec text-xs uppercase tracking-widest text-brand-navy">Commercial Service Plan</span>
          <h2 className="mt-3 font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-brand-ink">Commercial Monthly Maintenance</h2>
          <p className="mt-4 text-lg text-muted-foreground">Keeping commercial robotic mowers working — not waiting for something to go wrong.</p>
        </div>

        {/* Intro */}
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div className="space-y-4 text-muted-foreground leading-relaxed">
            <p>Commercial robotic mowers can operate for extremely long hours and are responsible for maintaining significantly larger areas than domestic machines. For sports clubs, businesses and commercial sites, downtime matters.</p>
            <p>Our Commercial Monthly Maintenance Plan is a proactive maintenance and support programme designed to keep commercial robotic mowers operating reliably throughout the mowing season.</p>
            <p>Instead of waiting for a problem to develop, we visit the site regularly to inspect, maintain and monitor the equipment.</p>
          </div>
          <div className="rounded-3xl overflow-hidden shadow-xl ring-1 ring-brand-navy/10">
            <img src={svc1} alt="Robotic Lawnmower Experts technicians servicing a commercial mower on site" className="w-full h-72 lg:h-80 object-cover" />
          </div>
        </div>

        {/* Three plan cards */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="rounded-2xl border border-brand-navy/10 bg-white p-6">
            <span className="grid place-items-center h-11 w-11 rounded-xl bg-brand-navy text-white"><Wrench className="h-5 w-5" /></span>
            <h3 className="mt-4 font-display font-semibold text-lg text-brand-navy">Monthly Maintenance Includes</h3>
            <CheckList items={MAINTENANCE} />
          </div>
          <div className="rounded-2xl border border-brand-navy/10 bg-white p-6">
            <span className="grid place-items-center h-11 w-11 rounded-xl bg-brand-navy text-white"><Headphones className="h-5 w-5" /></span>
            <h3 className="mt-4 font-display font-semibold text-lg text-brand-navy">Priority Support</h3>
            <p className="mt-2 text-sm text-muted-foreground">Commercial maintenance customers also benefit from:</p>
            <CheckList items={PRIORITY} />
          </div>
          <div className="rounded-2xl border border-brand-navy/10 bg-white p-6">
            <span className="grid place-items-center h-11 w-11 rounded-xl bg-brand-navy text-white"><Snowflake className="h-5 w-5" /></span>
            <h3 className="mt-4 font-display font-semibold text-lg text-brand-navy">Winter Service, Collection &amp; Secure Storage</h3>
            <p className="mt-2 text-sm text-muted-foreground">When the mowing season ends, your plan continues. Your plan includes:</p>
            <CheckList items={WINTER} />
          </div>
        </div>

        {/* Tailored + CTA */}
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
          <div className="rounded-3xl overflow-hidden shadow-xl ring-1 ring-brand-navy/10 order-last lg:order-first grid grid-cols-2 gap-1 bg-brand-navy/5">
            <img src={svc3} alt="Technician repairing a robotic mower in the workshop" className="w-full h-full object-cover" />
            <img src={svc4} alt="Robotic Lawnmower Experts team unboxing new Mammotion LUBA robotic mowers" className="w-full h-full object-cover" />
          </div>
          <div className="rounded-3xl bg-brand-navy text-white p-8 md:p-10 flex flex-col justify-center">
            <span className="grid place-items-center h-11 w-11 rounded-xl bg-white text-brand-navy"><Settings2 className="h-5 w-5" /></span>
            <h3 className="mt-5 font-display font-bold text-2xl">Commercial Plans Tailored to Your Site</h3>
            <p className="mt-3 text-white/80">Commercial installations vary considerably depending on the number of machines, site size, operating hours, travel distance and level of support required. We'll build a maintenance plan around your equipment and site requirements.</p>
            <Button onClick={onEnquire} className="mt-6 w-fit bg-white hover:bg-white/90 text-brand-navy font-semibold" data-testid="maintenance-enquire-button">
              Discuss a Commercial Maintenance Plan
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
